import { Copy } from 'lucide-react'
import { useMemo } from 'react'
import { useToast } from '../../../contexts/NotificationContext'
import { formatDateToBrazilian } from '../../../utils/formatUtils'
import { calcularValor, somarMinutosApontamentos } from '../../../utils/faturamentoCalculo'
import type {
  EtapaFaturamentoDto,
  FaturamentoColaboradorResumo,
  FaturamentoDetalhesDto,
  FaturamentoDto,
} from '../services/faturamento'

export interface FaturamentoDetalhesConteudoProps {
  detalhesFaturamento: FaturamentoDetalhesDto
  faturamentoSelecionado: FaturamentoDto | null
  etapasFaturamento: EtapaFaturamentoDto[]
  faturamentosProjeto: FaturamentoDto[]
  /**
   * Acompanhamento: recorte por linha (ex.: um time ou colaborador em suporte agregado).
   * Faturamento administrador: omitir ou false para manter o detalhamento completo do lançamento.
   */
  detalhesFiltradosPorLinhaAcompanhamento?: boolean
}

function formatarData(data?: string): string {
  if (!data) return '-'
  return formatDateToBrazilian(data) || '-'
}

function formatarMoeda(valor: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(valor)
}

function normalizarNomeComparacao(n: string | null | undefined): string {
  if (n == null || n === '') return ''
  return n
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
}

/**
 * Conteúdo exibido no modal "Detalhes do Faturamento" (administrador) e reutilizado no painel de hover do acompanhamento.
 */
export function FaturamentoDetalhesConteudo({
  detalhesFaturamento,
  faturamentoSelecionado,
  etapasFaturamento,
  faturamentosProjeto,
  detalhesFiltradosPorLinhaAcompanhamento = false,
}: FaturamentoDetalhesConteudoProps) {
  const toast = useToast()

  const { aplicarFiltroLinhaSuporte, apontamentosSuporteEfetivos, colaboradoresResumoExibicao } =
    useMemo(() => {
      const aplicar =
        detalhesFiltradosPorLinhaAcompanhamento === true &&
        detalhesFaturamento.idProjeto === 0 &&
        faturamentoSelecionado != null

      const nomes = new Set<string>()
      if (aplicar && faturamentoSelecionado) {
        const cr = faturamentoSelecionado.colaboradoresResumo
        if (cr && cr.length > 0) {
          for (const c of cr) {
            const k = normalizarNomeComparacao(c.nome)
            if (k) nomes.add(k)
          }
        }
        const ana = faturamentoSelecionado.analistaSMNNome
        if (ana) {
          const k = normalizarNomeComparacao(ana)
          if (k) nomes.add(k)
        }
      }

      const apontTodos = detalhesFaturamento.apontamentos ?? []
      const apEfetivos =
        aplicar && nomes.size > 0
          ? apontTodos.filter((ap) => nomes.has(normalizarNomeComparacao(ap.responsavelNome)))
          : apontTodos

      let colabExibicao: FaturamentoColaboradorResumo[] | undefined = detalhesFaturamento.colaboradoresResumo
      if (aplicar && faturamentoSelecionado) {
        const daLinha = faturamentoSelecionado.colaboradoresResumo
        if (daLinha && daLinha.length > 0) colabExibicao = daLinha
        else if (colabExibicao && nomes.size > 0) {
          colabExibicao = colabExibicao.filter((c) => nomes.has(normalizarNomeComparacao(c.nome)))
        }
      }

      return {
        aplicarFiltroLinhaSuporte: aplicar,
        apontamentosSuporteEfetivos: apEfetivos,
        colaboradoresResumoExibicao: colabExibicao,
      }
    }, [detalhesFiltradosPorLinhaAcompanhamento, detalhesFaturamento, faturamentoSelecionado])

  return (
    <div className="space-y-4 p-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-sm text-gray-600">Etapa de Faturamento</p>
          <p className="font-semibold text-gray-900">
            {faturamentoSelecionado?.codigoEtapaFaturamento
              ? etapasFaturamento.find(
                  (e) => e.codigoEtapaFaturamento === faturamentoSelecionado.codigoEtapaFaturamento,
                )?.descricao || '-'
              : '-'}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Nome do Projeto</p>
          <p className="font-semibold text-gray-900">{detalhesFaturamento.projetoNome}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Data de Recebimento</p>
          <p className="font-semibold text-gray-900">
            {formatarData(detalhesFaturamento.projetoDataRecebimento)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Responsável (Analista SMN)</p>
          <p className="font-semibold text-gray-900">
            {aplicarFiltroLinhaSuporte &&
            colaboradoresResumoExibicao &&
            colaboradoresResumoExibicao.length > 0
              ? [...new Set(colaboradoresResumoExibicao.map((c) => c.nome).filter(Boolean))].join(', ')
              : detalhesFaturamento.analistaSMNNome || '-'}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Tipo de Projeto</p>
          <p className="font-semibold text-gray-900">
            {detalhesFaturamento.idProjeto === 0
              ? 'Suporte'
              : detalhesFaturamento.tipoProjetoNome || '-'}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Valor Hora</p>
          <p className="font-semibold text-gray-900">{formatarMoeda(detalhesFaturamento.valorHora)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Data de Vigência do Valor Hora</p>
          <p className="font-semibold text-gray-900">
            {formatarData(detalhesFaturamento.valorHoraDataInicioValidade)}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-600">Usuário Solicitante</p>
          <p className="font-semibold text-gray-900">{detalhesFaturamento.solicitanteNome || '-'}</p>
        </div>
      </div>

      <div className="mt-4">
        <div className="bg-white rounded-lg border border-gray-200 shadow-theme-sm p-3">
          <h4 className="text-sm font-semibold text-gray-900 mb-3">Estimativa - Custo do Projeto</h4>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 text-xs">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">
                    Etapa
                  </th>
                  <th className="text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">
                    Horas
                  </th>
                  <th className="text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">
                    Minutos
                  </th>
                  <th className="text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b">Valor</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const isSuporte = detalhesFaturamento.idProjeto === 0
                  const valorHora = detalhesFaturamento.valorHora

                  let etapas: Array<{ nome: string; minutos: number }> = []

                  if (!isSuporte) {
                    let homologacaoMinutos = 0
                    const faturamentosHomologacao = faturamentosProjeto.filter(
                      (f) => f.codigoEtapaFaturamento === 6,
                    )
                    faturamentosHomologacao.forEach((fat) => {
                      if (fat.apontamentos && fat.apontamentos.length > 0) {
                        const minutos = fat.apontamentos.reduce(
                          (acc, entry) => acc + (entry.minutos || 0),
                          0,
                        )
                        homologacaoMinutos += minutos
                      } else if (fat.valorTotal > 0) {
                        const horas = fat.valorTotal / valorHora
                        homologacaoMinutos += horas * 60
                      }
                    })

                    etapas = [
                      { nome: 'Escopo', minutos: detalhesFaturamento.escopoOrcado ?? 0 },
                      { nome: 'Desenvolvimento', minutos: detalhesFaturamento.desenvolvimentoOrcado ?? 0 },
                      { nome: 'Testes', minutos: detalhesFaturamento.testesOrcado ?? 0 },
                      { nome: 'Homologação', minutos: homologacaoMinutos },
                    ]
                  } else {
                    let escopoMinutos = 0
                    let desenvolvimentoMinutos = 0
                    let testesMinutos = 0
                    let homologacaoMinutos = 0

                    const normalizarTag = (tag: string) =>
                      tag
                        .normalize('NFD')
                        .replace(/[\u0300-\u036f]/g, '')
                        .toUpperCase()
                        .trim()

                    const tagsEscopo = new Set<string>(['ANALISE', 'ESCOPO', 'ORCAMENTO'])
                    const tagsDesenvolvimento = new Set<string>(['DESENVOLVIMENTO'])
                    const tagsTestes = new Set<string>(['TESTE', 'TESTES'])
                    const tagsHomologacao = new Set<string>(['TESTE DE PRODUTO', 'HOMOLOGACAO'])

                    const apontamentos = isSuporte
                      ? apontamentosSuporteEfetivos
                      : (detalhesFaturamento.apontamentos ?? [])

                    apontamentos.forEach((ap) => {
                      const minutos = ap.minutos || 0
                      if (minutos <= 0) return
                      const tags = ap.tags ?? []

                      let categoria: 'Escopo' | 'Desenvolvimento' | 'Testes' | 'Homologacao' | 'Nenhuma' =
                        'Nenhuma'

                      for (const tag of tags) {
                        if (!tag) continue
                        const t = normalizarTag(tag)
                        if (tagsEscopo.has(t)) {
                          categoria = 'Escopo'
                          break
                        }
                        if (tagsDesenvolvimento.has(t)) {
                          categoria = 'Desenvolvimento'
                          break
                        }
                        if (tagsTestes.has(t)) {
                          categoria = 'Testes'
                          break
                        }
                        if (tagsHomologacao.has(t)) {
                          categoria = 'Homologacao'
                          break
                        }
                      }

                      if (categoria === 'Escopo') escopoMinutos += minutos
                      else if (categoria === 'Desenvolvimento') desenvolvimentoMinutos += minutos
                      else if (categoria === 'Testes') testesMinutos += minutos
                      else if (categoria === 'Homologacao') homologacaoMinutos += minutos
                    })

                    etapas = [
                      { nome: 'Escopo', minutos: escopoMinutos },
                      { nome: 'Desenvolvimento', minutos: desenvolvimentoMinutos },
                      { nome: 'Testes', minutos: testesMinutos },
                      { nome: 'Homologação', minutos: homologacaoMinutos },
                    ]
                  }

                  let totalMinutos = 0
                  let totalValor = 0

                  return (
                    <>
                      {etapas.map((etapa, index) => {
                        const horas = etapa.minutos / 60
                        const valor = calcularValor(etapa.minutos, valorHora)
                        totalMinutos += etapa.minutos
                        totalValor += valor

                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">
                              {etapa.nome}
                            </td>
                            <td className="py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                              {etapa.minutos > 0 ? `${horas.toFixed(2).replace('.', ',')}h` : '-'}
                            </td>
                            <td className="py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                              {etapa.minutos > 0 ? `${etapa.minutos} min` : '-'}
                            </td>
                            <td className="py-1.5 px-2 border-b text-xs text-gray-900 text-right">
                              {etapa.minutos > 0 ? formatarMoeda(valor) : '-'}
                            </td>
                          </tr>
                        )
                      })}
                      <tr className="bg-gray-100 font-semibold">
                        <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900">
                          Custo Total do Projeto
                        </td>
                        <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                          {totalMinutos > 0 ? `${(totalMinutos / 60).toFixed(2).replace('.', ',')}h` : '-'}
                        </td>
                        <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                          {totalMinutos > 0 ? `${totalMinutos} min` : '-'}
                        </td>
                        <td className="py-1.5 px-2 text-xs text-gray-900 text-right">
                          {totalMinutos > 0 ? formatarMoeda(totalValor) : '-'}
                        </td>
                      </tr>
                      {(() => {
                        const etapasEspeciais = [3, 4, 5]
                        const codigoEtapa = faturamentoSelecionado?.codigoEtapaFaturamento
                        const tipoProjetoNome = detalhesFaturamento.tipoProjetoNome
                        const usarCalculoBaseadoOrcamento =
                          tipoProjetoNome === 'Orçamento' &&
                          codigoEtapa !== undefined &&
                          etapasEspeciais.includes(codigoEtapa) &&
                          detalhesFaturamento.desenvolvimentoOrcado !== undefined &&
                          detalhesFaturamento.testesOrcado !== undefined

                        let apontamentosMinutos = 0
                        let apontamentosHoras = 0
                        let apontamentosValor = 0

                        if (usarCalculoBaseadoOrcamento && faturamentoSelecionado) {
                          apontamentosValor = faturamentoSelecionado.valorTotal
                          apontamentosHoras = apontamentosValor / valorHora
                          apontamentosMinutos = apontamentosHoras * 60
                        } else if (aplicarFiltroLinhaSuporte && faturamentoSelecionado) {
                          const th = faturamentoSelecionado.totalHoras
                          if (th != null && Number(th) > 0) {
                            apontamentosHoras = Number(th)
                            apontamentosMinutos = apontamentosHoras * 60
                            const vt = faturamentoSelecionado.valorTotal
                            apontamentosValor =
                              vt != null && Number(vt) >= 0
                                ? Number(vt)
                                : calcularValor(Math.round(apontamentosMinutos), valorHora)
                          } else if (apontamentosSuporteEfetivos.length > 0) {
                            apontamentosMinutos = somarMinutosApontamentos(apontamentosSuporteEfetivos)
                            apontamentosHoras = apontamentosMinutos / 60
                            apontamentosValor = calcularValor(apontamentosMinutos, valorHora)
                          } else {
                            return null
                          }
                        } else if (detalhesFaturamento.apontamentos && detalhesFaturamento.apontamentos.length > 0) {
                          const obsNorm = (detalhesFaturamento.observacao ?? '').toLowerCase()
                          const vtDet = detalhesFaturamento.valorTotal
                          const usarTotalFaturamentoNaLinhaVerde =
                            obsNorm.includes('planilha') &&
                            vtDet != null &&
                            Number(vtDet) > 0 &&
                            valorHora > 0

                          if (usarTotalFaturamentoNaLinhaVerde) {
                            apontamentosValor = Number(vtDet)
                            apontamentosHoras = apontamentosValor / valorHora
                            apontamentosMinutos = apontamentosHoras * 60
                          } else {
                            apontamentosMinutos = somarMinutosApontamentos(detalhesFaturamento.apontamentos)
                            apontamentosHoras = apontamentosMinutos / 60
                            apontamentosValor = calcularValor(apontamentosMinutos, valorHora)
                          }
                        } else {
                          return null
                        }

                        return (
                          <tr className="bg-green-200">
                            <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900">
                              Detalhes do Apontamento
                            </td>
                            <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                              {apontamentosMinutos > 0
                                ? `${apontamentosHoras.toFixed(2).replace('.', ',')}h`
                                : '-'}
                            </td>
                            <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                              {apontamentosMinutos > 0 ? `${Math.round(apontamentosMinutos)} min` : '-'}
                            </td>
                            <td className="py-1.5 px-2 text-xs text-gray-900 text-right">
                              {apontamentosMinutos > 0 ? formatarMoeda(apontamentosValor) : '-'}
                            </td>
                          </tr>
                        )
                      })()}
                    </>
                  )
                })()}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm text-gray-600">Observação</p>
          {detalhesFaturamento.observacao && (
            <button
              type="button"
              onClick={() => {
                const texto = detalhesFaturamento.observacao || ''
                if (navigator.clipboard) {
                  navigator.clipboard.writeText(texto).then(() => {
                    toast.success('Sucesso', 'Observação copiada para a área de transferência!')
                  })
                } else {
                  const el = document.createElement('textarea')
                  el.value = texto
                  el.style.position = 'fixed'
                  el.style.opacity = '0'
                  document.body.appendChild(el)
                  el.focus()
                  el.select()
                  document.execCommand('copy')
                  document.body.removeChild(el)
                  toast.success('Sucesso', 'Observação copiada para a área de transferência!')
                }
              }}
              className="p-1.5 text-gray-500 hover:text-brand-600 hover:bg-gray-100 rounded transition-colors"
              title="Copiar observação"
            >
              <Copy className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 relative">
          <p className="text-sm text-gray-900 whitespace-pre-wrap">
            {detalhesFaturamento.observacao || 'Nenhuma observação'}
          </p>
        </div>
      </div>

      {colaboradoresResumoExibicao && colaboradoresResumoExibicao.length > 0 && (
        <div className="mt-4">
          <div className="bg-white rounded-lg border border-gray-200 shadow-theme-sm p-3">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Resumo de horas por responsável</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300 text-xs">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">
                      Responsável
                    </th>
                    <th className="text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">
                      Horas
                    </th>
                    <th className="text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-r border-gray-300">
                      Minutos
                    </th>
                    <th className="text-right text-xs font-medium text-gray-700 py-1.5 px-2 border-b border-gray-300">
                      Valor
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const linhas = (colaboradoresResumoExibicao || [])
                      .map((colab) => ({
                        nome: colab.nome || 'Sem responsável',
                        minutos: colab.totalMinutos || 0,
                        horas: colab.horas || 0,
                        valor: colab.valor || 0,
                      }))
                      .filter((l) => l.minutos > 0)
                      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))

                    let totalMinutos = 0
                    let totalValor = 0

                    return (
                      <>
                        {linhas.map((linha, index) => {
                          totalMinutos += linha.minutos
                          totalValor += linha.valor
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900">
                                {linha.nome}
                              </td>
                              <td className="py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                {linha.minutos > 0 ? `${linha.horas.toFixed(2).replace('.', ',')}h` : '-'}
                              </td>
                              <td className="py-1.5 px-2 border-b border-r border-gray-300 text-xs text-gray-900 text-right">
                                {linha.minutos > 0 ? `${Math.round(linha.minutos)} min` : '-'}
                              </td>
                              <td className="py-1.5 px-2 border-b text-xs text-gray-900 text-right">
                                {linha.minutos > 0 ? formatarMoeda(linha.valor) : '-'}
                              </td>
                            </tr>
                          )
                        })}
                        <tr className="bg-gray-100 font-semibold">
                          <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900">Total</td>
                          <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                            {totalMinutos > 0
                              ? `${(totalMinutos / 60).toFixed(2).replace('.', ',')}h`
                              : '-'}
                          </td>
                          <td className="py-1.5 px-2 border-r border-gray-300 text-xs text-gray-900 text-right">
                            {totalMinutos > 0 ? `${Math.round(totalMinutos)} min` : '-'}
                          </td>
                          <td className="py-1.5 px-2 text-xs text-gray-900 text-right">
                            {totalMinutos > 0 ? formatarMoeda(totalValor) : '-'}
                          </td>
                        </tr>
                      </>
                    )
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
